import type { VariantContent } from './types';

export const cyberdogContent: VariantContent = {
  id: 'cyberdog',

  tagline: ['くらしを守る', 'サイバードッグ'],
  ctaBadge: '＼ 今なら初月無料！ ／',
  ctaText: '無料体験に申し込む',
  ctaHref: '#join',

  logoWebp: '/lp-img/logo.svg',
  logoWhiteSvg: '/lp-img/logo_white.svg',
  logoSignWhiteSvg: '/lp-img/logo_sign_white.svg',

  navItems: [
    { href: '#life', label: 'たっぷとの暮らし' },
    { href: '#about', label: 'たっぷとは' },
    { href: '#howto', label: 'みんなの使い方' },
    { href: '#pricing', label: '料金プラン' },
    { href: '#option', label: 'オプション' },
    { href: '#support', label: 'スマホなんでもサポート' },
    { href: '#flow', label: 'ご利用までの流れ' },
    { href: '#qa', label: 'よくあるご質問' },
    { href: '#join', label: 'お申込み' },
  ],

  showOption: true,
  showSupport: true,
  showCompare: false,
  showFraudStats: true,

  hero: {
    subtitle: ['くらしを守る', 'サイバードッグ'],
    logoSvg: '/lp-img/logo.svg',
    logoAlt: 'たっぷ',
    images: [
      '/lp-img/hero-main.webp',
      '/lp-img/hero-slide2.webp',
      '/lp-img/hero-slide3.webp',
    ],
    slideAlt: 'デジタルセラピードッグたっぷと過ごす暮らし',
    badges: [
      { label: '詐欺', sub: '対策' },
      { label: '話し', sub: '相手' },
      { label: '脳トレ', sub: '習慣' },
    ],
    catchcopy: ['すべてを', '一匹のデジタル愛犬で'],
  },

  about: {
    paragraphs: [
      { lines: ['ネットから、電話から、', '詐欺の脅威は暮らしの中に。', 'ひとりで気づくのは難しい。'] },
      { lines: ['ふだんはかわいい愛犬。', 'いざとなれば、サイバー番犬。', 'それがたっぷです。'], bold: true },
    ],
    image: '/lp-img/life-photos.webp',
    imageAlt: 'たっぷと過ごすシニアの暮らし',
  },

  life: {
    en: 'life',
    heading: ['ひとり暮らしの毎日に', 'もう一匹の家族を'],
  },

  fraudStats: {
    en: 'facts',
    heading: ['あなたの大切な家族が', '狙われています'],
    stats: [
      { value: '1,354億円', label: '年間の特殊詐欺被害額', emphasis: true },
      { value: '22,612件', label: '年間の認知件数' },
      { value: '約599万円', label: '1件あたりの平均被害額', emphasis: true },
    ],
    seniorNote: '被害者の約78%が高齢者です',
    comparisonHeading: 'でも、たっぷなら',
    chartDamageValue: '599万円',
    chartDamageLabel: '1件あたりの平均被害額',
    chartTappuValue: '1,980円/月',
    chartTappuLabel: 'たっぷの月額',
    ratioValue: '約 1/3,000',
    ratioNote: 'の費用で大切なご家族を詐欺から守れます',
    source: '出典：警察庁「令和6年における特殊詐欺の認知・検挙状況等について」',
  },

  howto: {
    heading: 'こんな場面で',
    cards: [
      {
        number: '使い方①',
        title: '怪しいメールが来たとき',
        description: '話すボタンを押してたっぷに相談すれば、危険度をすぐに判定。ひとりで判断できない不安なメールや電話も、身近な相棒がそばにいれば安心です。',
        color: '#da627d',
        icon: '/lp-img/howto-icon1.webp',
      },
      {
        number: '使い方②',
        title: '毎朝の脳トレ習慣に',
        description: '最新の詐欺手口や対処法をクイズ形式で毎日出題。遊び感覚で続けるうちに、防犯の知識が自然と身につきます。脳トレと防犯がひとつの習慣に。',
        color: '#68b684',
        icon: '/lp-img/howto-icon2.webp',
      },
      {
        number: '使い方③',
        title: 'ひとりの時間がさみしいとき',
        description: '声にも顔にも反応してくれるたっぷ。ごはんをあげたり、撫でたり、話しかけたり――小さなお世話の積み重ねが、毎日の楽しみと愛着を育てます。',
        color: '#4560c2',
        icon: '/lp-img/howto-icon3.webp',
      },
      {
        number: '使い方④',
        title: '離れた家族が気になるとき',
        description: 'たっぷに触れたログが見守りアプリで確認でき、何かあればお知らせが届きます。服薬リマインダーも搭載。「監視」ではなく「気にかけ合う」つながりです。',
        color: '#da627d',
        icon: '/lp-img/howto-icon4.webp',
      },
    ],
  },

  pricing: {
    heroImage: '/lp-img/pricing-hero.webp',
    plans: [
      {
        id: 'basic_monthly',
        badge: '基本プラン',
        initialPrice: { label: '初期費用', value: '0', large: true, unit: '円' },
        monthlyPrice: { label: '月額費用', value: '1,980', accent: true, unit: '円', tax: true, note: '初回1カ月無料' },
        features: {
          title: '基本機能',
          items: [
            '育てる / お世話する / 可愛がる',
            '音声認識 / 顔認識',
            'バーチャル散歩機能（15分）',
            '詐欺の危険度お知らせ相談ボタン',
            '防犯対策クイズ／脳トレゲーム',
          ],
        },
        accordion: {
          title: '初回無料体験とは？',
          answer: '初回1カ月間は、すべての機能を完全無料でお試しいただけます。体験期間中の機能制限はありません。1カ月経過後、公式LINEまたはお電話にて継続のご意向を確認いたします。継続されない場合、返却時の送料も無料です。',
          note: '※2カ月目以降は、月額費用が発生します。',
        },
      },
      {
        id: 'family_mimamori',
        badge: '家族見守りプラン',
        initialPrice: { label: '初期費用', value: '0', large: true, unit: '円' },
        monthlyPrice: { label: '月額費用', value: '2,980', accent: true, unit: '円', tax: true, note: '初回1カ月無料' },
        features: {
          title: '基本機能 + 見守り機能',
          items: [
            '基本機能のもの全て',
            '見守りアプリ',
            '服薬などのリマインダー機能',
            '見守り緊急通知機能',
          ],
        },
        appPromo: {
          images: [
            { src: '/lp-img/mimamori-app.webp', alt: '見守りアプリ画面1' },
            { src: '/lp-img/mimamori-app2.webp', alt: '見守りアプリ画面2' },
            { src: '/lp-img/mimamori-app3.webp', alt: '見守りアプリ画面3' },
          ],
          title: '見守りアプリ',
          description: '離れて暮らすご家族がスマホで利用状況を確認できます。たっぷに触れたログや、緊急時のお知らせが届きます。',
        },
        accordion: {
          title: '初回無料体験とは？',
          answer: '初回1カ月間は、すべての機能を完全無料でお試しいただけます。体験期間中の機能制限はありません。1カ月経過後、公式LINEまたはお電話にて継続のご意向を確認いたします。継続されない場合、返却時の送料も無料です。',
          note: '※2カ月目以降は、月額費用が発生します。',
        },
      },
    ],
    pricingFaqs: [
      { question: 'プラン変更はいつでもできますか？', answer: 'はい、ネットからいつでも変更可能です。変更は翌月から適用されます。' },
      { question: '家族見守りアプリは何台まで使えますか？', answer: '台数制限はありません。ご家族のスマホで自由にご利用いただけます。' },
    ],
    packageImage: '/lp-img/package-photo.webp',
    packageItems: [
      'デジタルペット専用端末',
      'USB-C 充電セット',
      '取扱説明書',
    ],
    productSubtitle: 'デジタルペット専用タブレットの詳細な特徴や仕様を説明します。',
    productDetails: [
      { item: 'ディスプレイ', text: '10.1インチ HD（1280 × 800）IPS液晶' },
      { item: 'サイズ', text: '約 241.5 × 149.4 × 8.3mm' },
      { item: '重さ', text: '約 420g' },
      { item: 'プロセッサ', text: 'MediaTek Helio P22T（オクタコア、最大2.3GHz）' },
      { item: '通信規格', text: 'Wi-Fi 802.11ac（2.4GHz / 5GHz）、Bluetooth 5.0' },
      { item: 'バッテリー', text: '5,000mAh' },
    ],
    notices: [
      'タブレット端末であるため、直接日光が当たる場所には置かないようにしてください。タブレットが熱くなると故障の原因になります。',
      '随時アップデートで改善を続けています。万一不具合が発生した場合は、運営までご連絡ください。お客様のデータをそのまま保持したまま、新しい端末に交換してお送りいたします。',
      '常に最新のアプリのバージョンをご利用ください。（アップデート時には案内を送付いたします。）',
    ],
  },

  pricingPhoto: {
    roundTop: '/lp-img/round_bottom_blue.svg',
    image: '/lp-img/pricing-photo.webp',
    roundBottom: '/lp-img/round_top_white.svg',
  },

  flow: {
    steps: [
      {
        number: '1',
        title: 'お申し込み',
        description: 'アプリ、またはネットから直接お申し込みください。',
        link: { text: 'お申込みはこちら', href: '#join' },
      },
      {
        number: '2',
        title: 'お届け',
        description: '数営業日以内に、サイバードッグの専用キットでご自宅へ配送します。',
      },
      {
        number: '3',
        title: '設置',
        description: '設置サポートをお申込みの方には、設置スタッフが訪問。日時をご指定ください。お客様ご自身でも簡単に設置できます。',
      },
      {
        number: '4',
        title: '使い始め',
        description: 'サイバードッグのお名前を決めて、使い始めましょう！まずはお腹が空いているので、えさをあげて、愛情を与えましょう。',
      },
    ],
  },

  qa: {
    items: [
      { question: '契約の縛りや解約金はありますか？', answer: '縛りも違約金もありません。解約は電話・Web・公式LINEから可能です（解約月は満額・日割りなし）。' },
      { question: '初回無料体験について教えてください', answer: '初回1カ月間は完全無料でお試しいただけます。体験期間は1カ月で、期間終了後に継続のご意向を確認いたします。返却時の送料も無料です。' },
      { question: '端末について教えてください', answer: '常時電源につないで使うタブレット端末で、電気代は月30円程度です。自然故障は無償交換（データは保持）、落下・水濡れなどの場合は有償です。機械が苦手な方でもお世話できるシンプルな仕様です。' },
      { question: '見守りアプリで何がわかりますか？', answer: 'ご家族がスマホで利用状況やたっぷに触れたログを確認でき、異変時はお知らせが届きます。' },
    ],
  },

  join: {
    en: 'join',
    sectionId: 'join',
    heading: 'お申込み',
    subText: '返却時の送料も無料です',
    ctaBadge: '＼ 今なら初月無料！ ／',
    ctaText: '無料体験に申し込む',
    registrationSource: 'landing_page',
    deviceBannerNote: '※月額1,980円のサブスクリプションへの加入が必要です。',
    successMessage: '確認メールとパスワード設定メールを送信しました。',
  },

  footer: {
    dogImage: '/lp-img/footer-dog.webp',
    dogImageAlt: 'デジタルセラピードッグたっぷ',
    copyright: '© ぴちぴち株式会社 All Rights Reserved.',
  },

  seo: {
    title: 'デジタルセラピードッグ たっぷ | 初回1カ月無料・詐欺対策×話し相手×脳トレ',
    description: 'たっぷは、詐欺対策・話し相手・脳トレ習慣をひとつにしたデジタルセラピードッグ。離れて暮らすシニアのご家族をやさしく見守ります。初回1カ月無料体験実施中。返却無料。',
    ogTitle: 'デジタルセラピードッグ たっぷ | 初回1カ月無料・詐欺対策×話し相手×脳トレ',
    ogDescription: '詐欺対策・話し相手・脳トレ習慣をひとつにしたデジタルセラピードッグ。初回1カ月無料体験実施中。',
    ogImage: 'https://digitalpet.jp/lp-img/hero-main.webp',
    twitterTitle: 'デジタルセラピードッグ たっぷ | 初回1カ月無料',
    twitterDescription: '詐欺対策・話し相手・脳トレ習慣をひとつに。初回1カ月無料体験実施中。',
    canonical: 'https://digitalpet.jp/',
  },
};
